import { COMMA, ENTER, SEMICOLON, SPACE } from '@angular/cdk/keycodes';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import {
  Frequency,
  FrequencyModel,
  Product,
  WatchAlert,
} from '@compumark/order-management-client';

import { EmailValidator } from '../../preferences/validators/email-validator';
import { AlertGridOptionsService } from '../alert-columns/alert-column-definition/alert-grid-options.service';
import { sanitizeEmojiString } from '../utils/sanitize-input.util';

@Component({
  selector: 'app-alert-settings',
  templateUrl: './alert-settings.component.html',
  styleUrls: ['./alert-settings.component.scss'],
  standalone: false,
})
export class AlertSettingsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private data = inject<WatchAlert>(MAT_DIALOG_DATA);
  private formBuilder = inject(FormBuilder);
  private alertOptionsService = inject(AlertGridOptionsService);

  @ViewChild('tooltip') errorTooltip!: MatTooltip;
  public alertSettingsForm!: FormGroup;
  public separatorKeysCodes: number[] = [COMMA, SPACE, ENTER, SEMICOLON];
  public isError = false;
  public tooltipMessage!: string;
  public alertTitleMaxLength = 100;
  public isMonthlyDelivery = false;
  public frequency = Frequency.WEEKLY.toUpperCase();
  public selectedDays: string[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
  ];

  private monthlyDeliveredProducts: number[] = [1532, 1533, 1535, 1566];
  private usedTitles: Set<string> = new Set<string>();
  private hasBeenDestroyed = false;
  private tooltipTimeoutId?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.alertOptionsService.getGridApi()?.forEachNode((node: any) => {
      this.usedTitles.add(node?.data?.title);
    });

    this.setInitialFrequency();

    this.alertSettingsForm = this.formBuilder.group({
      alertTitle: [this.data?.title ?? '', this.duplicateTitleValidator()],
      alertSubject: [this.data?.subject ?? ''],
      ccAlert: [[], EmailValidator.email()],
      bccAlert: [[], EmailValidator.email()],
    });
    this.alertSettingsForm
      .get('alertTitle')!
      .valueChanges.subscribe((value) => {
        const sanitized = sanitizeEmojiString(value);
        if (sanitized !== value) {
          this.alertSettingsForm
            .get('alertTitle')!
            .setValue(sanitized, { emitevent: true });
        }
        this.refreshTooltip();
        this.data.title = sanitized.trim();
      });
    this.alertSettingsForm
      .get('alertSubject')!
      .valueChanges.subscribe((value) => {
        const sanitized = sanitizeEmojiString(value);
        if (sanitized !== value) {
          this.alertSettingsForm
            .get('alertSubject')!
            .setValue(sanitized, { emitevent: true });
        }
        this.data.subject = sanitized.trim();
      });
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      if (!this.hasBeenDestroyed) {
        this.refreshTooltip();
      }
    });
  }

  ngOnDestroy(): void {
    this.hasBeenDestroyed = true;
    if (this.tooltipTimeoutId) {
      clearTimeout(this.tooltipTimeoutId);
      this.tooltipTimeoutId = undefined;
    }
  }

  onFrequencyChange(newFrequency: string): void {
    if (newFrequency) {
      this.frequency = newFrequency;
      this.updateFrequency();
    }
  }

  onSelectedDaysChange(days: string[]): void {
    if (days && days.length > 0) {
      this.selectedDays = days;
      this.updateFrequency();
    }
  }

  onChipsChange(newChips: string[], type: string): void {
    if (!newChips) {
      return;
    }

    if (type === 'cc') {
      this.data.cc = newChips;
    } else if (type === 'bcc') {
      this.data.bcc = newChips;
    }
  }

  updateFrequency(): void {
    this.data.frequency = this.isMonthlyDelivery
      ? ({
          type: Frequency.MONTHLY.toUpperCase(),
          days: ['monday'],
        } as FrequencyModel)
      : ({
          type: this.frequency,
          days: this.selectedDays,
        } as FrequencyModel);
  }

  private determineMonthlyDelivery(): void {
    const products = this.data?.products ?? [];
    this.isMonthlyDelivery = products.every((product: Product) =>
      this.monthlyDeliveredProducts.includes(product.productId!),
    );
  }

  setInitialFrequency(): void {
    if (
      this.data?.frequency &&
      this.data?.frequency?.type !== Frequency.MONTHLY.toUpperCase()
    ) {
      this.frequency = this.data.frequency.type!.toUpperCase();
      this.selectedDays = this.data.frequency.days!;
    }
    this.determineMonthlyDelivery();
    this.updateFrequency();
  }

  private duplicateTitleValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trim().toLowerCase();
      if (!value) {
        return null;
      }

      const isDuplicate = Array.from(this.usedTitles).some(
        (title) => title.trim().toLowerCase() === value,
      );

      return isDuplicate ? { duplicateTitle: true } : null;
    };
  }

  private refreshTooltip(): void {
    const control = this.alertSettingsForm.get('alertTitle');
    if (!control) {
      return;
    }
    const value = control.value?.trim() ?? '';
    if (value.length >= this.alertTitleMaxLength) {
      this.showErrorTooltip('Alert title cannot exceed 100 characters.');
      return;
    } else if (control.hasError('duplicateTitle')) {
      this.showErrorTooltip(
        'That title is already being used for another Alert.',
      );
    } else {
      this.hideErrorTooltip();
    }
  }

  private showErrorTooltip(message: string): void {
    this.isError = true;
    this.tooltipMessage = message;
    if (this.hasBeenDestroyed || !this.errorTooltip) {
      return;
    }
    this.errorTooltip.disabled = false;
    this.tooltipTimeoutId = setTimeout(() => {
      if (!this.hasBeenDestroyed && this.errorTooltip) {
        this.errorTooltip.show();
      }
    });
  }

  private hideErrorTooltip(): void {
    this.isError = false;
    if (this.tooltipTimeoutId) {
      clearTimeout(this.tooltipTimeoutId);
      this.tooltipTimeoutId = undefined;
    }
    if (this.hasBeenDestroyed || !this.errorTooltip) {
      return;
    }
    this.errorTooltip.hide();
    this.errorTooltip.disabled = true;
  }

  get bccEmails(): string[] {
    return this.data?.bcc ?? [];
  }

  get ccEmails(): string[] {
    return this.data?.cc ?? [];
  }
}
